<template>
    <div>
        <div class="card">
            <div class="card-body z-10">
                <div class="d-flex justify-content-between align-items-center">
                    <h4 class="mb-2 text-lg font-medium">Users</h4>

                    <div>
                        <a class="btn btn-primary mb-2 mr-2" @click.prevent="showAddUserForm">Add User</a>

                        <button class="btn pull-right btn-default dropdown-toggle" type="button"
                                id="dropdownMenuButton" data-toggle="dropdown">Invite Guest
                        </button>

                        <div class="dropdown-menu dropdown-menu-right">
                            <button class="dropdown-item" @click.prevent="showAddUserForm('tenant')">Tenant</button>
                            <button class="dropdown-item" @click.prevent="showAddUserForm('landlord')">Landlord</button>
                        </div>
                    </div>
                </div>

                <div class="form-inline">
                    <!--Text search filter-->
                    <div class="input-group my-2 mr-2">
                        <input type="text"
                               v-model="searchParams.search_text"
                               @keyup.enter="fetchUsers"
                               placeholder="Search Users"
                               class="form-control">
                        <div class="input-group-append">
                            <button class="btn btn-primary" type="button" @click="fetchUsers">
                                Search
                            </button>
                        </div>
                    </div>

                    <!--filter roles-->
                    <el-select v-model="searchParams.roles"
                               class="input-group my-2 mr-2"
                               placeholder="Filter By Role"
                               @change="fetchUsers"
                               multiple
                               collapse-tags>
                        <el-option v-for="role in allRoles" :key="role" :value="role" :label="role | capitalize"></el-option>
                    </el-select>

                    <!--Status filter-->
                    <el-select v-model="searchParams.status"
                               @change="fetchUsers"
                               class="input-group my-2 mr-2"
                               style="width: 140px"
                               placeholder="Select Status">
                        <el-option v-for="status in userStatuses" :key="status" :value="status" :label="status | capitalize"></el-option>
                    </el-select>

                    <!--Team/Guest Filter-->
                    <el-select v-model="searchParams.user_type"
                               @change="fetchUsers"
                               class="input-group my-2 mr-2"
                               style="width: 140px"
                               placeholder="Select Group">
                        <el-option key="team_users" label="Team Users" value="team_users"></el-option>
                        <el-option key="guest_users" label="Guest Users" value="guest_users"></el-option>
                    </el-select>
                </div>

                <div class="mt-2 table-responsive">
                    <table class="table ">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody v-if="users.data.length >= 1 && !loading">
                            <tr v-for="(user, index) in users.data"
                                :class="{'bg-light-danger' : user.team_user_status === 'inactive'}">
                                <td>{{ index + 1 }}</td>
                                <td>{{ user.name }}</td>
                                <td>{{ user.email }}</td>
                                <td>{{ user.role }}</td>
                                <td>
                                    <a :href="urlForUpdatingUser(user.id)"
                                       class="btn btn-rounded btn-outline-primary btn-sm pull-right">
                                        View
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="users.data.length < 1 && !loading" class="text-gray-500 flex justify-center">No users with those filters</div>

                    <div v-if="loading" class="text-gray-500 flex justify-center items-center">
                        <icon-refresh class="h-4 w-4 animate-spin text-indigo-600"/> &nbsp;Loading...
                    </div>

                    <div class="d-flex justify-content-between">
                        <div>
                            <a href="#" @click.prevent="requestMoreUsers" class="text-muted pull-right" v-if="searchParams.user_type === 'team_users'">
                                {{ team.subscription.user_seats_used }} active {{ team.subscription.user_seats_used > 1 ? "users" : "user" }}. {{ team.subscription.remaining_user_seats }} users remaining.
                            </a>
                        </div>

                        <el-pagination
                            layout="prev, pager, next"
                            :total="users.meta.total"
                            :page-size="Number(users.meta.per_page)"
                            :current-page="users.meta.current_page"
                            @current-change="fetchUsers">
                        </el-pagination>
                    </div>
                </div>
            </div>
        </div>

        <create-user-form v-if="showAddUserModal"
                          :team="team"
                          :roles="assignableRoles"
                          :mailboxes="mailboxes"
                          @close="closeAddUserForm">
        </create-user-form>
    </div>
</template>

<script>
export default {
    props: ["team"],

    data () {
        return {
            users: {
                data: [],
                meta: {},
            },
            searchParams: {
                status: "All",
                user_type: "team_users",
                roles: "",
            },
            userStatuses: ["All", "Active", "Inactive", "Pending"],
            allRoles: [],
            assignableRoles: [],
            mailboxes: [],
            showAddUserModal: false,
            loading: true,
        };
    },

    mounted () {
        this.fetchUsers();
        this.getAssignableRolesAndMailboxes();
        this.getAllRoles();
    },

    methods: {
        fetchUsers: _.debounce(function (page = 1) {
            this.loading = true;
            this.searchParams.page = page;
            this.searchParams.per_page = 20;
            this.searchParams.orderForCompanySettingsUsers = true;

            axios.get("/users", {
                params: this.searchParams
            })
            .then(response => {
                this.users = response.data;
                this.loading = false;
            })
            .catch(errors => {
                this.loading = false;
                toastr.error(errors.message);
            });

        }, 300),

        urlForUpdatingUser (userId) {
            return "/users/profile/" + userId;
        },

        getAllRoles () {
            axios.get(this.team.id + "/users/roles-filter")
            .then(response => {
                this.allRoles = response.data.roles;
            });
        },

        // get all roles that the current user has permission to assign and mailboxes
        // will return only Tenant or Landlord when inviting guests
        getAssignableRolesAndMailboxes (selectedRole = null) {
            axios.get(this.team.id + "/users/create?selected_role=" + selectedRole)
            .then(response => {
                this.assignableRoles = response.data.roles;
                this.mailboxes = response.data.mailboxes;
            });
        },

        showAddUserForm (selectedRole = null) {
            // request more users if necessary
            if (this.team.subscription.remaining_user_seats === 0 && !["landlord", "tenant"].includes(selectedRole)) {
                this.requestMoreUsers();
                return;
            }

            this.getAssignableRolesAndMailboxes(selectedRole);

            if (this.assignableRoles !== []) {
                this.showAddUserModal = true;
            }
        },

        closeAddUserForm () {
            this.showAddUserModal = false;
        },

        requestMoreUsers () {
            if (this.app.state.pricing_plan === "limited") {
                swal({
                    title: "Creating more users not allowed on Limited accounts.",
                    text: "Consider upgrading to a premium account to add more users!",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Yes, Let's upgrade!",
                    cancelButtonText: "Remind me later"
                }).then((result) => {
                    if (result.value) {
                        window.location = "/settings#/tab-billing";
                    }
                });
            } else {
                let title = this.team.subscription.remaining_user_seats === 0 ? "You've reached your user limit" : "You have " + this.team.subscription.remaining_user_seats + " " + (this.team.subscription.remaining_user_seats > 1 ? "users" : "user") + " left";
                swal({
                    title: title,
                    type: "info",
                    text: "Contact us at info@redrabbit.zone to purchase more users."
                });
            }
        },
    }
};
</script>
